import { useState, useEffect } from 'react';

// File validation constants
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];

export const useMediaUpload = (width, height) => {
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedVideo, setUploadedVideo] = useState(null);
  const [videoFrames, setVideoFrames] = useState([]);
  const [currentVideoFrame, setCurrentVideoFrame] = useState(0);
  const [originalImageData, setOriginalImageData] = useState(null);

  const validateFile = (file, allowedTypes) => {
    // Check if file exists
    if (!file) {
      return { valid: false, error: 'No file selected' };
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        error: `File too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB`
      };
    }

    // Check file type
    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: `Invalid file type. Allowed types: ${allowedTypes.join(', ')}`
      };
    }

    return { valid: true };
  };

  // Re-process image when dimensions change
  useEffect(() => {
    if (originalImageData) {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        canvas.width = width;
        canvas.height = height;

        ctx.drawImage(img, 0, 0, width, height);
        const imageData = ctx.getImageData(0, 0, width, height);

        setVideoFrames([imageData.data]);
        setCurrentVideoFrame(0);
      };
      img.src = originalImageData;
    }
  }, [width, height, originalImageData]);

  const handleImageUpload = async (e, setCustomPattern, setMode) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file
    const validation = validateFile(file, ALLOWED_IMAGE_TYPES);
    if (!validation.valid) {
      alert(validation.error);
      e.target.value = ''; // Reset input
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const imageSrc = event.target.result;
      setOriginalImageData(imageSrc);

      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        canvas.width = width;
        canvas.height = height;

        ctx.drawImage(img, 0, 0, width, height);
        const imageData = ctx.getImageData(0, 0, width, height);

        setVideoFrames([imageData.data]);
        setCurrentVideoFrame(0);
        setCustomPattern(() => () => 0);

        setMode('creative');
        setUploadedImage(URL.createObjectURL(file));
        setUploadedVideo(null);
      };
      img.src = imageSrc;
    };
    reader.readAsDataURL(file);
  };
  
  const handleVideoUpload = (e, setCustomPattern, setMode) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file
    const validation = validateFile(file, ALLOWED_VIDEO_TYPES);
    if (!validation.valid) {
      alert(validation.error);
      e.target.value = ''; // Reset input
      return;
    }

    alert('Processing video... This may take a moment.');
    
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = 'anonymous';
    
    let capturedFrames = [];
    let isCapturing = false;
    
    const startCapture = async () => {
      if (isCapturing) return;
      isCapturing = true;
      
      canvas.width = width;
      canvas.height = height;
      
      const fps = 10;
      const maxDuration = Math.min(video.duration, 10);
      const totalFrames = Math.floor(maxDuration * fps);
      
      for (let i = 0; i < totalFrames; i++) {
        const time = (i / fps);
        
        try {
          video.currentTime = time;
          
          await new Promise((resolve) => {
            const onSeeked = () => {
              video.removeEventListener('seeked', onSeeked);
              resolve();
            };
            video.addEventListener('seeked', onSeeked);
            setTimeout(resolve, 200);
          });
          
          ctx.drawImage(video, 0, 0, width, height);
          const imageData = ctx.getImageData(0, 0, width, height);
          capturedFrames.push(new Uint8ClampedArray(imageData.data));
          
        } catch (err) {
          console.error('Error capturing frame', i, err);
        }
      }
      
      if (capturedFrames.length > 0) {
        setVideoFrames(capturedFrames);
        setCurrentVideoFrame(0);
        setCustomPattern(() => () => 0);
        setMode('creative');
        setUploadedVideo(URL.createObjectURL(file));
        setUploadedImage(null);
        alert(`Video loaded! ${capturedFrames.length} frames captured.`);
      } else {
        alert('Failed to capture video frames. Please try a different video file.');
      }
    };
    
    video.onloadedmetadata = () => {
      video.play().then(() => {
        video.pause();
        video.currentTime = 0;
        setTimeout(startCapture, 500);
      }).catch(err => {
        setTimeout(startCapture, 500);
      });
    };
    
    video.onerror = (e) => {
      alert('Failed to load video. Please try MP4 format with smaller file size.');
    };
    
    const url = URL.createObjectURL(file);
    video.src = url;
    video.load();
  };

  const clearMedia = () => {
    setUploadedImage(null);
    setUploadedVideo(null);
    setVideoFrames([]);
    setCurrentVideoFrame(0);
    setOriginalImageData(null);
  };

  return {
    uploadedImage,
    uploadedVideo,
    videoFrames,
    currentVideoFrame,
    setCurrentVideoFrame,
    handleImageUpload,
    handleVideoUpload,
    clearMedia
  };
};